import { z } from 'zod';
import type { Level } from './topics';

/*
 * Learning paths: three starting points through Nirvik's library, by how much
 * someone already knows. Shown on the landing page, at /paths, and on Home,
 * where a learner can pick one (saved as `users.learning_path`). Each path is
 * a timeline: every library course and quiz that fits it, in a suggested
 * order, named by slug (anything not published is skipped), then "Find more"
 * into Explore filtered by level. Curated by hand for now; recommendations
 * and live filters by level and tags can replace the timeline later.
 */

export const LEARNING_PATHS = ['first-steps', 'level-up', 'build-on'] as const;
export type LearningPath = (typeof LEARNING_PATHS)[number];
export const learningPathSchema = z.enum(LEARNING_PATHS);

/** Old path ids (Build On was Deep End until October 2026), kept for shared links. */
export const RENAMED_PATHS: Record<string, LearningPath> = { 'deep-end': 'build-on' };

/** A path id from a URL or a saved choice, following renames; undefined if unknown. */
export function toLearningPath(raw: unknown): LearningPath | undefined {
  const id = typeof raw === 'string' ? (RENAMED_PATHS[raw] ?? raw) : raw;
  return learningPathSchema.safeParse(id).data;
}

export interface PathStep {
  kind: 'course' | 'quiz';
  slug: string;
  /** Why this step, in a few words ("Start here: a 10-minute check"). */
  why: string;
}

export interface PathInfo {
  id: LearningPath;
  name: string;
  /** One line under the name. */
  tagline: string;
  /** Who it's for, in plain words. */
  definition: string;
  /** "This is you if…", in the learner's own words. */
  example: string;
  /** Catalog levels for upcoming events; the first is "Find more"'s filter. */
  levels: Level[];
  /** Everything on the path, in a suggested order. */
  timeline: PathStep[];
  /** A note under the timeline (Build On: where the hands-on material is). */
  note?: string;
  /** Offered only on request (Build On: AI Red Team). */
  restricted?: { title: string; description: string; contact: string };
}

export const PATH_INFO: Record<LearningPath, PathInfo> = {
  'first-steps': {
    id: 'first-steps',
    name: 'First Steps',
    tagline: 'New to security? Start here.',
    definition:
      'You use apps every day, and maybe build them with AI tools, but words like phishing, MFA or API key still feel new. No technical background needed.',
    example: 'I built my first app with Lovable and I’m not sure what’s safe to put online.',
    levels: ['beginner'],
    timeline: [
      { kind: 'quiz', slug: 'cyber-safety-check', why: 'A quick check of your everyday habits' },
      { kind: 'course', slug: 'spot-the-scam', why: 'How today’s scams work, and what to do' },
      {
        kind: 'course',
        slug: 'ai-at-work',
        why: 'Use ChatGPT, Copilot and other AI tools at work safely',
      },
      {
        kind: 'course',
        slug: 'security-foundations',
        why: 'The basics every builder needs, in plain words',
      },
      { kind: 'quiz', slug: 'cyber-month-2026', why: 'Test yourself on everything so far' },
      {
        kind: 'course',
        slug: 'protect-your-family',
        why: 'Protect your parents and family from today’s scams',
      },
      { kind: 'course', slug: 'secure-vibe-coding', why: 'If you build with AI app builders' },
      { kind: 'course', slug: 'secure-automations', why: 'If you connect apps with automations' },
      { kind: 'course', slug: 'privacy-for-builders', why: 'If your app holds personal data' },
      { kind: 'course', slug: 'first-24-hours', why: 'Be ready for a bad day' },
      {
        kind: 'course',
        slug: 'securing-ai-code',
        why: 'If you write code with AI assistants: your bridge to Level Up',
      },
    ],
  },
  'level-up': {
    id: 'level-up',
    name: 'Level Up',
    tagline: 'You know the basics. Go further.',
    definition:
      'You know the common risks and some fixes. Now you want to understand how attacks really work, and secure what you build properly.',
    example:
      'I use passkeys and keep secrets out of code, but I couldn’t explain how SSRF or prompt injection works.',
    levels: ['intermediate'],
    timeline: [
      {
        kind: 'course',
        slug: 'securing-ai-code',
        why: 'Review what AI writes, and rein in coding agents',
      },
      {
        kind: 'course',
        slug: 'security-fundamentals',
        why: 'Web app security, mapped to the OWASP Top 10',
      },
      { kind: 'course', slug: 'secure-llm-apps', why: 'Defend chatbots, RAG and agents' },
      { kind: 'course', slug: 'cloud-defense', why: 'Secure your cloud account' },
      { kind: 'course', slug: 'agentic-ai-workshop', why: 'See how agents are built' },
      {
        kind: 'course',
        slug: 'cyber-security-master-class',
        why: 'The bigger picture, from real attacks to the AI frontier',
      },
    ],
  },
  'build-on': {
    id: 'build-on',
    name: 'Build On',
    tagline: 'Hands-on labs and real tools.',
    definition:
      'You ship code or run infrastructure, and you’re ready to build on what you know: hands-on labs, real security tools, and testing your own systems the way an attacker would.',
    example:
      'I deploy to AWS from CI and want to scan our Terraform, work through labs, and harden our AI agents.',
    levels: ['intermediate', 'advanced'],
    timeline: [
      {
        kind: 'course',
        slug: 'security-fundamentals',
        why: 'Do the lab track: fix a vulnerable app in Node.js or Python',
      },
      {
        kind: 'course',
        slug: 'cloud-defense',
        why: 'Fix misconfigured Terraform, then try the AWS sandbox track',
      },
      {
        kind: 'course',
        slug: 'secure-llm-apps',
        why: 'The deep dives: secure RAG, tool permissions and guardrails',
      },
      {
        kind: 'course',
        slug: 'detection-engineering',
        why: 'Write and test detections in Sigma, with a hands-on lab',
      },
      { kind: 'course', slug: 'agentic-ai-workshop', why: 'Build and test a multi-agent system' },
      {
        kind: 'course',
        slug: 'securing-ai-code',
        why: 'Scanning tools and supply chain deep dives',
      },
      {
        kind: 'course',
        slug: 'cyber-security-master-class',
        why: 'Hands-on labs with real security tools',
      },
    ],
    note: 'The labs and deep dives are optional lessons inside these courses: open a course and look for the lab tracks and Go Deeper modules.',
    restricted: {
      title: 'AI Red Team',
      description:
        'Simulate attacks on your own AI systems and build the defenses. Available only to authorized teams.',
      contact: 'redteaming@nirvik.ai',
    },
  },
};

/** An item on a path, as visitors who aren't signed in see it. */
export interface PublicPathItem {
  kind: 'course' | 'quiz';
  slug: string;
  title: string;
  summary: string | null;
  level: Level;
  minutes: number | null;
  lessonCount: number | null;
  questionCount: number | null;
}

/*
 * "Where should I start?": a short placement quiz at /paths/start. Each
 * answer leans toward a path (0 First Steps, 1 Level Up, 2 Build On); the
 * average picks one. Self-report plus light knowledge checks, so nobody
 * "fails" it. Nothing is stored except the path a learner chooses to save.
 */
export interface PlacementQuestion {
  id: string;
  prompt: string;
  options: { label: string; lean: 0 | 1 | 2 }[];
}

export const PLACEMENT_QUESTIONS: PlacementQuestion[] = [
  {
    id: 'build',
    prompt: 'What best describes what you build?',
    options: [
      { label: 'I don’t build apps; I use them', lean: 0 },
      { label: 'I build with AI app builders or no-code tools', lean: 0 },
      { label: 'I write code, often with AI help', lean: 1 },
      { label: 'I ship code to production and run infrastructure', lean: 2 },
    ],
  },
  {
    id: 'phishing',
    prompt: 'Could you explain how a phishing link takes over an account?',
    options: [
      { label: 'Not really', lean: 0 },
      { label: 'Roughly: a fake page steals the password', lean: 1 },
      { label: 'Yes, including stolen sessions that get past MFA', lean: 2 },
    ],
  },
  {
    id: 'api-key',
    prompt: 'Someone mentions an “API key”. You…',
    options: [
      { label: 'Aren’t sure what that is', lean: 0 },
      { label: 'Know it’s a secret that shouldn’t be shared', lean: 1 },
      { label: 'Know how to store, scope and rotate one', lean: 2 },
    ],
  },
  {
    id: 'sign-in',
    prompt: 'How do you sign in to your most important accounts?',
    options: [
      { label: 'Mostly passwords', lean: 0 },
      { label: 'Passwords plus a code by text or an app', lean: 1 },
      { label: 'Passkeys or security keys wherever I can', lean: 2 },
    ],
  },
  {
    id: 'access',
    prompt: 'How do you make sure one user of your app can’t see another user’s data?',
    options: [
      { label: 'I don’t have an app, or I’m not sure', lean: 0 },
      { label: 'I rely on the platform’s settings', lean: 1 },
      { label: 'Checks on the server, tested with two accounts', lean: 2 },
    ],
  },
  {
    id: 'owasp',
    prompt: 'Have you used the OWASP Top 10?',
    options: [
      { label: 'Never heard of it', lean: 0 },
      { label: 'I’ve heard of it', lean: 1 },
      { label: 'I use it when reviewing code', lean: 2 },
    ],
  },
  {
    id: 'cloud-bill',
    prompt: 'Your cloud bill jumps overnight. What would you do first?',
    options: [
      { label: 'I don’t use the cloud, or I’m not sure', lean: 0 },
      { label: 'Contact support and change passwords', lean: 1 },
      { label: 'Deactivate the leaked key and check the audit logs in every region', lean: 2 },
    ],
  },
  {
    id: 'goal',
    prompt: 'What do you want from your next hour of learning?',
    options: [
      { label: 'Stay safe and understand the basics', lean: 0 },
      { label: 'Understand attacks and secure what I build', lean: 1 },
      { label: 'Hands-on labs and real tools', lean: 2 },
    ],
  },
];

/**
 * The suggested path for a set of answers (question id to option index).
 * Unanswered questions are ignored; with no answers, First Steps.
 */
export function placementResult(answers: Record<string, number>): LearningPath {
  const leans = PLACEMENT_QUESTIONS.flatMap((q) => {
    const option = q.options[answers[q.id] ?? -1];
    return option ? [option.lean] : [];
  });
  if (!leans.length) return 'first-steps';
  const average = leans.reduce<number>((sum, lean) => sum + lean, 0) / leans.length;
  return average < 0.75 ? 'first-steps' : average < 1.4 ? 'level-up' : 'build-on';
}
